import { useEffect, useMemo, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';

import { useDocumentUpdateStatus } from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext.ts';
import { fg } from '@confluence/feature-gating';
import { setGraphqlErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';

const extractTraceId = (errorHtml: string): string | null => {
	// Extract 32-character hex ID (trace ID format: 946aa64e691d4fab808bb6287f6e1e90)
	// This is localization-independent - works regardless of error message language
	const match = errorHtml.match(/([a-f0-9]{32})/);
	return match ? match[1] : null;
};

export const useADFDocument = (adf: string, isFabric: boolean): [DocNode] => {
	const { documentUpdated } = useDocumentUpdateStatus();
	const docParsed = useMemo(() => {
		let documentParsed: DocNode = {
			version: 1,
			type: 'doc',
			content: [],
		};

		if (adf && isFabric) {
			try {
				documentParsed = JSON.parse(adf);
			} catch {
				let errorString = 'Failed to parse ADF';

				if (
					adf.startsWith('<div class="error fatal-render-error">') &&
					fg('confluence_include_error_in_adf_parse_failure')
				) {
					errorString += `: ${adf}`;
				}

				const traceIdFromHtml = adf.startsWith('<div class="error fatal-render-error">')
					? extractTraceId(adf)
					: null;

				const error = new Error(errorString);

				if (traceIdFromHtml && fg('confluence_surface_trace_id_in_adf_error')) {
					setGraphqlErrorAttributes(error, {
						operationName: 'contentBody',
						traceId: traceIdFromHtml,
					});
				}

				throw error;
			}
		}
		return documentParsed;
	}, [adf, isFabric]);

	const [document, setDocument] = useState(docParsed);

	useEffect(() => {
		setDocument(docParsed);
	}, [docParsed, documentUpdated]);
	return [document];
};
